// InvoiceViewer.jsx

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

// Context
import { useBase } from 'context/BaseContext';
import { useUser } from 'context/UserContext';

// Hooks
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';

// CSS
import 'nodetypes/purchaseorder/view/PurchaseOrderViewer.css';
import './InvoiceViewer.css';

// Local
import { createDocumentFromSource } from 'nodetypes/documents/create/createDocumentFromSource';
import DocumentPayments from 'nodetypes/documents/payments/DocumentPayments';
import DocumentRichTextSection from 'nodetypes/documents/kit/DocumentRichTextSection';
import useDocumentRelatedNodes from 'nodetypes/documents/hooks/useDocumentRelatedNodes';
import { issueInvoice } from 'nodetypes/invoice/lifecycle/issueInvoice';
import {
  canLogInvoicePayments,
  isInvoiceIssued,
  isInvoiceStructureLocked,
  isInvoiceVoided,
} from 'nodetypes/invoice/lifecycle/invoiceLifecycleModel';
import VoidInvoiceModal from 'nodetypes/invoice/lifecycle/VoidInvoiceModal';
import InvoiceGeneralInfo from 'nodetypes/invoice/view/InvoiceGeneralInfo';
import InvoiceItems from 'nodetypes/invoice/view/InvoiceItems';
import InvoicePreviewModal from 'nodetypes/invoice/view/InvoicePreviewModal';
import InvoiceQboBadge from 'nodetypes/invoice/view/InvoiceQboBadge';

// Components
import Button from 'components/button/Button';
import ContextMenu from 'components/contextmenu/ContextMenu';

/**
 * InvoiceViewer
 *
 * Workspace Highlight-slot screen for INVOICE.
 *
 * @param {object} node
 * @param {object|null} [nodeType]
 * @param {boolean} [readOnly=false]
 * @param {function} [onNodeChange]
 * @param {function|null} [onRelatedNodeOpen]
 * @param {object[]|null} [neighborNodes]
 * @param {object|null} [letterheadOverride]
 * @returns {ReactNode}
 */
const InvoiceViewer = React.memo(({
  node,
  nodeType = null,
  readOnly = false,
  onNodeChange,
  onRelatedNodeOpen = null,
  neighborNodes: neighborNodesProp = null,
  letterheadOverride = null,
}) => {
  // ----- CONTEXT ----- //

  const { activeBase } = useBase();
  const { user } = useUser();
  const { openExpanded } = useNodeOpenIntent();

  // ----- STATE ----- //

  const [previewOpen, setPreviewOpen] = useState(false);
  const [voidMode, setVoidMode] = useState(null); // null | 'void' | 'duplicate'
  const [issueBusy, setIssueBusy] = useState(false);
  const actionsTriggerRef = useRef(null);

  // ----- MEMOS / HOOKS ----- //

  const baseKey = String(activeBase?.key || node?.baseKey || '').trim();
  const subscribedNeighbors = useDocumentRelatedNodes(
    neighborNodesProp == null ? node?.key : null,
    neighborNodesProp == null ? baseKey : null,
  );
  const neighborNodes = neighborNodesProp == null ? subscribedNeighbors : neighborNodesProp;

  const issued = useMemo(() => isInvoiceIssued(node, nodeType), [node, nodeType]);
  const voided = useMemo(() => isInvoiceVoided(node, nodeType), [node, nodeType]);
  const structureLocked = useMemo(
    () => isInvoiceStructureLocked(node, nodeType),
    [node, nodeType],
  );
  const canLogPayments = useMemo(
    () => canLogInvoicePayments(node, nodeType),
    [node, nodeType],
  );

  const itemsReadOnly = Boolean(readOnly || structureLocked);
  const paymentsStructureLocked = Boolean(readOnly || structureLocked);
  const generalReadOnly = Boolean(readOnly || voided);
  const canManageLifecycle = Boolean(!readOnly && node?.key && user?.key);

  // ----- HANDLERS ----- //

  const handleIssue = useCallback(async () => {
    if (!canManageLifecycle || issueBusy || issued || voided) return;
    setIssueBusy(true);
    try {
      await issueInvoice({ invoiceNode: node, invoiceType: nodeType });
      toast.success('Invoice issued.', { autoClose: 3000 });
    } catch (error) {
      toast.error(error?.message || 'Could not issue invoice.', { autoClose: 4000 });
    } finally {
      setIssueBusy(false);
    }
  }, [canManageLifecycle, issueBusy, issued, node, nodeType, voided]);

  const handleVoided = useCallback(async ({ duplicate }) => {
    if (!duplicate || !user?.key || !node?.key) return;
    try {
      const created = await createDocumentFromSource({
        sourceNode: node,
        targetTypeKey: 'INVOICE',
        user,
        baseKey: baseKey || null,
        sourceLineNodes: neighborNodes,
        includePayments: false,
      });
      toast.success('Draft duplicate created.', { autoClose: 3000 });
      if (created) openExpanded(created);
    } catch (error) {
      toast.error(error?.message || 'Invoice voided, but duplicate failed.', {
        autoClose: 5000,
      });
    }
  }, [baseKey, neighborNodes, node, openExpanded, user]);

  if (!node) return null;

  // ----- RENDER ----- //

  return (
    <div className="purchase-order-viewer">
      <div className="invoice-viewer-toolbar">
        <div className="invoice-viewer-toolbar-start">
          {voided ? (
            <span className="invoice-viewer-status-pill is-void">Voided</span>
          ) : issued ? (
            <span className="invoice-viewer-status-pill is-issued">Issued</span>
          ) : (
            <span className="invoice-viewer-status-pill is-draft">Draft</span>
          )}
          <InvoiceQboBadge
            node={node}
            canManage={canManageLifecycle}
          />
        </div>

        <div className="invoice-viewer-toolbar-end">
          {canManageLifecycle && !issued && !voided ? (
            <Button
              label={issueBusy ? 'Issuing…' : 'Issue'}
              iconKey="task_alt"
              height="30px"
              enabled={!issueBusy}
              onClick={handleIssue}
            />
          ) : null}

          {canManageLifecycle && issued && !voided ? (
            <span ref={actionsTriggerRef} className="invoice-viewer-actions-trigger">
              <Button
                label="Actions"
                endIconKey="keyboard_arrow_down"
                endIconSize="18px"
                height="30px"
                gap="4px"
              />
              <ContextMenu
                triggerMode="click"
                triggerRef={actionsTriggerRef}
              >
                <ContextMenu.Item
                  label="Void…"
                  onClick={() => setVoidMode('void')}
                />
                <ContextMenu.Item
                  label="Void & Duplicate…"
                  onClick={() => setVoidMode('duplicate')}
                />
              </ContextMenu>
            </span>
          ) : null}

          <Button
            label="Preview"
            iconKey="visibility"
            height="30px"
            onClick={() => setPreviewOpen(true)}
          />
        </div>
      </div>

      <section className="purchase-order-viewer-section">
        <div className="purchase-order-viewer-section-header">
          <div className="purchase-order-viewer-section-title">
            General Info
          </div>
        </div>
        <InvoiceGeneralInfo
          node={node}
          nodeType={nodeType}
          readOnly={generalReadOnly}
          onNodeChange={onNodeChange}
          letterheadOverride={letterheadOverride}
        />
      </section>

      <section className="purchase-order-viewer-section">
        <InvoiceItems
          node={node}
          nodeType={nodeType}
          readOnly={itemsReadOnly}
          neighborNodes={neighborNodes}
          onRelatedNodeOpen={onRelatedNodeOpen}
        />
      </section>

      <section className="purchase-order-viewer-section">
        <DocumentPayments
          node={node}
          nodeType={nodeType}
          readOnly={readOnly || voided}
          structureLocked={paymentsStructureLocked}
          canLogPayments={canLogPayments && !readOnly}
          neighborNodes={neighborNodes}
          onRelatedNodeOpen={onRelatedNodeOpen}
        />
      </section>

      <section className="purchase-order-viewer-section">
        <DocumentRichTextSection
          node={node}
          nodeType={nodeType}
          readOnly={generalReadOnly}
          onNodeChange={onNodeChange}
          fieldName="termsAndConditions"
          title="Terms and Conditions"
          emptyLabel="No terms and conditions."
        />
      </section>

      <section className="purchase-order-viewer-section">
        <DocumentRichTextSection
          node={node}
          nodeType={nodeType}
          readOnly={generalReadOnly}
          onNodeChange={onNodeChange}
          fieldName="memo"
          title="Memo"
          emptyLabel="No memo."
        />
      </section>

      <InvoicePreviewModal
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        node={node}
        nodeType={nodeType}
        neighborNodes={neighborNodes}
        letterheadOverride={letterheadOverride}
      />

      <VoidInvoiceModal
        isOpen={Boolean(voidMode)}
        onClose={() => setVoidMode(null)}
        invoiceNode={node}
        invoiceType={nodeType}
        neighborNodes={neighborNodes}
        duplicate={voidMode === 'duplicate'}
        onVoided={handleVoided}
      />
    </div>
  );
});

// Dev Tools
InvoiceViewer.displayName = 'InvoiceViewer';

export default InvoiceViewer;
